import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ApiError, FaceViewer, initials, normalisePhone, prettyPhone, useAction,
} from '@limuzin/kit';
import type { MeView, StartedProofView } from '@limuzin/passenger';
import {
  changePassword, changePhone, removeAvatar, rename, setAvatar, startPhoneProof,
} from '../api';
import { useAvatar, useSession } from '../session';
import { Icon } from '../material';
import { pickPhotos } from '../photos';
import {
  Button, Chevron, ErrorNote, IconButton, Initials, LabelledField, ListRow, PasswordPair,
  PhoneField, Sheet, Spinner, SwipeToConfirm, showProblem,
} from '../widgets';
import { PhotoFrame } from './photo-frame';
import { REMOVE_ACCOUNT_PATH, RemoveAccountSheet, useCloseRemoval } from './remove-account';
import { ProofScreen } from './proof';
import { TownList } from './town';

type SheetName = 'name' | 'phone' | 'password' | 'town' | 'photo' | null;

/**
 * «Профиль» — who the driver will be told is waiting, and the rating that comes back
 * the other way. A port of `profile_screen.dart`.
 */
export function ProfileScreen() {
  const { me, setMe, refreshMe, signOut, supportUnread } = useSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const closeRemoval = useCloseRemoval('/profile');
  const [sheet, setSheet] = useState<SheetName>(null);

  if (!me) return <Spinner />;

  const displayName = [me.firstName, me.lastName].filter(Boolean).join(' ') || 'Гость';

  return (
    <div className="screen-scroll">
      <div className="column profile">
        <div className="row" style={{ gap: 14 }}>
          <Avatar me={me} onChanged={setMe} />
          <div className="grow">
            <button type="button" className="name-edit" onClick={() => setSheet('name')}>
              <span className="ellipsis">{displayName}</span>
              <Icon name="edit_outlined" size={15} style={{ color: 'var(--faint)' }} />
            </button>
            <div className="row" style={{ gap: 5, marginTop: 5 }}>
              <Icon name="star" size={14} style={{ color: 'var(--destination)', marginLeft: 0 }} />
              <span className="profile-rating">{(me.passenger?.rating ?? 5).toFixed(1)}</span>
            </div>
          </div>
          <IconButton
            icon="settings_outlined"
            label="Настройки"
            tone="card"
            style={{ color: 'var(--body)' }}
            onClick={() => navigate('/settings')}
          />
        </div>

        {/* Every row opens something, so none of them spells its own act out: the
            chevron says «this leads somewhere» once, in the same voice. */}
        <div className="list-card" style={{ marginTop: 20 }}>
          <ListRow
            title="Номер телефона"
            hint={prettyPhone(me.phone)}
            trailing={<Chevron />}
            onClick={() => setSheet('phone')}
          />
          <ListRow
            title="Пароль"
            hint="••••••••"
            trailing={<Chevron />}
            onClick={() => setSheet('password')}
          />
          <ListRow
            title="Мой город"
            hint={me.passenger?.town?.name ?? 'Не выбран — без него нет адресов и тарифа'}
            trailing={<Chevron />}
            onClick={() => setSheet('town')}
          />
          <ListRow
            title="Поддержка"
            // An answer waiting is the red dot on the envelope, not a line of its own.
            hint="Вопросы и ответы"
            trailing={
              supportUnread > 0 ? (
                <span
                  className="support-mail"
                  role="img"
                  aria-label={`Открыть переписку с поддержкой, ${supportUnread} не прочитано`}
                >
                  <Icon name="mail_outline" size={24} />
                </span>
              ) : (
                <Chevron />
              )
            }
            onClick={() => navigate('/support')}
          />
        </div>

        {/* The one act here that ends something is the one act that cannot be done
            with a tap: the knob has to be carried the length of the track. */}
        <div style={{ marginTop: 20 }}>
          <SwipeToConfirm
            label="Выйти из аккаунта"
            hint="Проведите вправо"
            armedHint="Отпустите — выйдете из аккаунта"
            icon="logout_rounded"
            onConfirm={signOut}
          />
        </div>
        {/* Last and quiet: nobody looks for it until they mean to leave, and the sheet
            behind it asks for the password before anything goes. It is a route of its
            own so that a link can open it — the one given to Google Play. */}
        <p style={{ textAlign: 'center', margin: '18px 0 0' }}>
          <button type="button" className="linkish quiet" onClick={() => navigate(REMOVE_ACCOUNT_PATH)}>
            Удалить аккаунт
          </button>
        </p>
      </div>

      {sheet === 'name' && (
        <NameSheet
          first={me.firstName}
          last={me.lastName ?? ''}
          onClose={() => setSheet(null)}
          onSaved={(who) => {
            setMe(who);
            setSheet(null);
          }}
        />
      )}
      {sheet === 'town' && (
        // «Мой город» is the passenger's own, not a preference of the app, so it is
        // changed here — the same list the sign-in gate uses.
        <Sheet
          title="Ваш город"
          hint="От города зависят адреса в подсказках и тариф."
          onClose={() => setSheet(null)}
        >
          <TownList onChosen={() => setSheet(null)} />
        </Sheet>
      )}
      {sheet === 'phone' && (
        <PhoneSheet
          onClose={() => setSheet(null)}
          onSaved={async () => {
            await refreshMe();
            setSheet(null);
          }}
        />
      )}
      {sheet === 'password' && (
        <PasswordSheet onClose={() => setSheet(null)} onSaved={signOut} />
      )}
      {pathname === REMOVE_ACCOUNT_PATH && (
        <RemoveAccountSheet onClose={closeRemoval} />
      )}
    </div>
  );
}

/**
 * The face at the top of «Профиль»: the photograph once there is one, the initials
 * until then, and the badge in the corner that says the circle is a button. The sheet
 * behind it holds everything that can be done to the face, taking it down included.
 */
function Avatar({ me, onChanged }: { me: MeView; onChanged: (me: MeView) => void }) {
  const { api } = useSession();
  const face = useAvatar(me.avatarKey);
  const circle = useRef<HTMLButtonElement>(null);
  const [asking, setAsking] = useState(false);
  const [viewing, setViewing] = useState<DOMRect | null>(null);
  const [framing, setFraming] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (act: () => Promise<MeView>) => {
    setBusy(true);
    try {
      onChanged(await act());
    } catch (problem) {
      showProblem(problem instanceof ApiError ? problem.message : 'Не удалось сохранить фото');
    } finally {
      setBusy(false);
    }
  };

  const pick = async (camera: boolean) => {
    setAsking(false);
    const [file] = await pickPhotos(camera ? { camera: 'user' } : {});
    // The passenger chooses the part of the photograph that goes in the circle; what
    // is sent is that square, not the whole picture.
    if (file) setFraming(file);
  };

  return (
    <>
      <button
        ref={circle}
        type="button"
        className="avatar"
        disabled={busy}
        onClick={() => setAsking(true)}
        aria-label="Фото профиля"
      >
        <Initials text={initials(me.firstName, me.lastName ?? undefined)} size={58} tint src={face} enlarges={false} />
        {busy && (
          <span className="avatar-busy">
            <span className="spinner" style={{ width: 20, height: 20 }} />
          </span>
        )}
        {/* Bottom-right, over the edge, ringed in the page's own colour — that ring
            is what keeps the badge off a dark photograph behind it. */}
        <span className="avatar-badge">
          <Icon name="photo_camera_outlined" size={12} />
        </span>
      </button>
      {asking && (
        <PhotoSource
          title="Фото профиля"
          hint="Его увидят водители, которым доступен ваш заказ."
          allowRemove={me.avatarKey !== null}
          onView={
            face
              ? () => {
                  setAsking(false);
                  // Grown out of this circle, as any other avatar's face is.
                  setViewing(circle.current?.getBoundingClientRect() ?? null);
                }
              : undefined
          }
          onGallery={() => void pick(false)}
          onCamera={() => void pick(true)}
          onRemove={() => {
            setAsking(false);
            void run(() => removeAvatar(api));
          }}
          onClose={() => setAsking(false)}
        />
      )}
      {viewing &&
        face &&
        createPortal(<FaceViewer src={face} from={viewing} onClose={() => setViewing(null)} />, document.body)}
      {framing && (
        <PhotoFrame
          file={framing}
          title="Фото профиля"
          onFramed={(framed) => {
            setFraming(null);
            void run(() => setAvatar(api, framed));
          }}
          onClose={() => setFraming(null)}
        />
      )}
    </>
  );
}

/**
 * «Из галереи», «Снять камерой» and, where there is a face, «Открыть фото» and
 * «Удалить фото».
 */
export function PhotoSource({
  title,
  hint,
  allowRemove = false,
  onGallery,
  onCamera,
  onView,
  onRemove,
  onClose,
}: {
  title: string;
  hint?: string;
  allowRemove?: boolean;
  onGallery: () => void;
  onCamera: () => void;
  /** Opens the face that is up there big; without it there is no «Открыть фото». */
  onView?: () => void;
  onRemove?: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet title={title} hint={hint} onClose={onClose}>
      <div className="stack" style={{ gap: 10 }}>
        <Button size="card" icon="photo_library_outlined" onClick={onGallery}>
          Из галереи
        </Button>
        <Button size="card" tone="plain" icon="photo_camera_outlined" onClick={onCamera}>
          Снять камерой
        </Button>
        {/* With the face that is up there, next to taking it down. */}
        {onView && (
          <Button size="card" tone="plain" icon="visibility_outlined" onClick={onView}>
            Открыть фото
          </Button>
        )}
        {/* Last, and in the red this app gives to what takes something away. */}
        {allowRemove && onRemove && (
          <Button size="card" tone="stop" icon="delete_outline" onClick={onRemove}>
            Удалить фото
          </Button>
        )}
      </div>
    </Sheet>
  );
}

function NameSheet({
  first: initialFirst,
  last: initialLast,
  onClose,
  onSaved,
}: {
  first: string;
  last: string;
  onClose: () => void;
  onSaved: (me: MeView) => void;
}) {
  const { api } = useSession();
  const [first, setFirst] = useState(initialFirst);
  const [last, setLast] = useState(initialLast);

  const save = useAction(async () => {
    if (!first.trim()) return;
    onSaved(await rename(api, first.trim(), last.trim()));
  });

  return (
    <Sheet title="Имя и фамилия" hint="Водитель видит только имя." onClose={onClose}>
      <div className="stack" style={{ gap: 10 }}>
        <LabelledField
          label="Имя"
          autoFocus
          autoComplete="given-name"
          value={first}
          onChange={(event) => setFirst(event.target.value)}
        />
        <LabelledField
          label="Фамилия"
          optional
          autoComplete="family-name"
          value={last}
          onChange={(event) => setLast(event.target.value)}
        />
      </div>
      <ErrorNote error={save.error} />
      <div style={{ marginTop: 14 }}>
        <Button
          size="card"
          busy={save.pending}
          disabled={!first.trim()}
          onClick={() => void save.run()}
        >
          Сохранить
        </Button>
      </div>
    </Sheet>
  );
}

/**
 * «Новый номер» — the number, then «Подтвердите номер» with the code texted to it
 * (ADR 0052), then the move. The old number stops signing in the moment it is made.
 */
function PhoneSheet({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { api } = useSession();
  const [phone, setPhone] = useState('');
  const [proving, setProving] = useState<{ phone: string; started: StartedProofView } | null>(
    null,
  );

  const normalised = normalisePhone(phone);

  const start = useAction(async () => {
    if (!normalised) throw new ApiError('bad_phone', 'Введите номер телефона полностью');
    setProving({ phone: normalised, started: await startPhoneProof(api, normalised) });
  });

  const save = useAction(async (verificationToken: string) => {
    await changePhone(api, verificationToken);
    await onSaved();
  });

  return (
    <>
      <Sheet
        title="Новый номер"
        hint="Подтвердите новый номер кодом из SMS. Старый перестанет работать для входа."
        onClose={onClose}
      >
        <PhoneField
          value={phone}
          onChange={setPhone}
          autoFocus
          onEnter={() => {
            if (normalised) void start.run();
          }}
        />
        <ErrorNote error={start.error ?? save.error} />
        <div style={{ marginTop: 14 }}>
          <Button
            size="card"
            busy={start.pending || save.pending}
            disabled={!normalised}
            onClick={() => void start.run()}
          >
            Подтвердить номер
          </Button>
        </div>
      </Sheet>
      {proving &&
        createPortal(
          <div className="proof-page">
            <ProofScreen
              phone={proving.phone}
              started={proving.started}
              restart={() => startPhoneProof(api, proving.phone)}
              onProved={async (token) => {
                setProving(null);
                await save.run(token);
              }}
              onBack={() => setProving(null)}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * «Смена пароля». Changing it revokes every session, this browser's included, so the
 * honest end of it is the sign-in screen rather than a tab that falls over on its own
 * an access token later.
 */
function PasswordSheet({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { api } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [shown, setShown] = useState(false);

  // With the eye open there is nothing left to confirm: the passenger is looking at
  // what they typed, so the confirmation stops being asked for.
  const ready = current.length > 0 && next.length >= 6 && (shown || next === repeat);
  const mismatch = !shown && repeat.length > 0 && next !== repeat;

  const save = useAction(async () => {
    if (!ready) return;
    await changePassword(api, current, next);
    onSaved();
  });

  return (
    <Sheet
      title="Смена пароля"
      hint="Новый пароль — минимум 6 символов. После смены вход на всех устройствах потребует его — включая это."
      onClose={onClose}
    >
      <div className="stack" style={{ gap: 10 }}>
        <LabelledField
          label="Текущий пароль"
          type={shown ? 'text' : 'password'}
          autoComplete="current-password"
          autoFocus
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <PasswordPair
          label="Новый пароль"
          repeatLabel="Повторите новый пароль"
          password={next}
          repeat={repeat}
          shown={shown}
          onToggle={() => setShown(!shown)}
          onPassword={setNext}
          onRepeat={setRepeat}
        />
      </div>
      {mismatch && (
        <p className="body" style={{ margin: '8px 0 0 2px', fontSize: 13.5, color: 'var(--stop-text)' }}>
          Пароли не совпадают
        </p>
      )}
      <ErrorNote error={save.error} />
      <div style={{ marginTop: 14 }}>
        <Button size="card" busy={save.pending} disabled={!ready} onClick={() => void save.run()}>
          Сохранить пароль
        </Button>
      </div>
    </Sheet>
  );
}
